import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Calendar,
  ChevronDown,
  Download,
  RefreshCcw,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  UserPlus,
  Percent,
  BarChart3,
  PieChart,
  Award,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type DateRange = 'today' | 'week' | 'month' | 'quarter' | 'year'

export function AffiliateReportPage() {
  const [dateRange, setDateRange] = useState<DateRange>('month')

  const dateRangeLabels: Record<DateRange, string> = {
    today: 'วันนี้',
    week: 'สัปดาห์นี้',
    month: 'เดือนนี้',
    quarter: 'ไตรมาสนี้',
    year: 'ปีนี้',
  }

  // Mock data
  const summaryStats = {
    totalAffiliates: 156,
    activeAffiliates: 128,
    totalCommission: 8450000,
    pendingCommission: 580000,
    totalMembers: 12580,
    newMembers: 856,
    totalDeposit: 158500000,
    avgConversion: 7.8,
  }

  const topAffiliates = [
    { rank: 1, name: 'สมชาย ใจดี', code: 'AFF001', members: 1250, deposit: 15800000, commission: 2850000, growth: 15.2 },
    { rank: 2, name: 'วิชัย มั่งมี', code: 'AFF002', members: 856, deposit: 9500000, commission: 1580000, growth: 12.5 },
    { rank: 3, name: 'พิมพ์ใจ สวยงาม', code: 'AFF003', members: 425, deposit: 4200000, commission: 720000, growth: 8.3 },
    { rank: 4, name: 'อนุชา รวยเร็ว', code: 'AFF004', members: 312, deposit: 3100000, commission: 520000, growth: 22.1 },
    { rank: 5, name: 'สุภาพร สดใส', code: 'AFF005', members: 285, deposit: 2800000, commission: 480000, growth: -5.2 },
  ]

  const levelDistribution = [
    { level: 'VIP', icon: '👑', count: 12, percent: 7.7, commission: 2850000 },
    { level: 'Platinum', icon: '💎', count: 28, percent: 17.9, commission: 2100000 },
    { level: 'Gold', icon: '🥇', count: 45, percent: 28.8, commission: 1580000 },
    { level: 'Silver', icon: '🥈', count: 38, percent: 24.4, commission: 1200000 },
    { level: 'Bronze', icon: '🥉', count: 33, percent: 21.2, commission: 720000 },
  ]

  const monthlyTrend = [
    { month: 'ม.ค.', affiliates: 120, members: 8500, commission: 5200000 },
    { month: 'ก.พ.', affiliates: 125, members: 9200, commission: 5800000 },
    { month: 'มี.ค.', affiliates: 132, members: 9800, commission: 6500000 },
    { month: 'เม.ย.', affiliates: 138, members: 10500, commission: 7200000 },
    { month: 'พ.ค.', affiliates: 145, members: 11200, commission: 7800000 },
    { month: 'มิ.ย.', affiliates: 156, members: 12580, commission: 8450000 },
  ]

  const performanceMetrics = [
    { label: 'Conversion Rate เฉลี่ย', value: '7.8%', change: '+0.5%', positive: true },
    { label: 'ค่าใช้จ่ายต่อสมาชิก (CPA)', value: '฿672', change: '-฿25', positive: true },
    { label: 'รายได้ต่อสมาชิก', value: '฿12,600', change: '+฿850', positive: true },
    { label: 'ROI พันธมิตร', value: '1,876%', change: '+125%', positive: true },
  ]

  const formatNumber = (num: number) => new Intl.NumberFormat('th-TH').format(num)
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const maxCommission = Math.max(...monthlyTrend.map(m => m.commission))

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">รายงานพันธมิตร</h1>
          <p className="text-muted-foreground">สรุปประสิทธิภาพและผลงานพันธมิตร</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Calendar className="h-4 w-4" />
                {dateRangeLabels[dateRange]}
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {Object.entries(dateRangeLabels).map(([key, label]) => (
                <DropdownMenuItem key={key} onClick={() => setDateRange(key as DateRange)}>
                  {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="icon">
            <RefreshCcw className="h-4 w-4" />
          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            ส่งออก
          </Button>
        </div>
      </div>

      {/* Main Stats */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <Badge variant="secondary" className="bg-win/10 text-win border-0 gap-1">
                <TrendingUp className="h-3 w-3" />
                +8
              </Badge>
            </div>
            <div className="mt-3">
              <p className="text-xs text-muted-foreground">พันธมิตรทั้งหมด</p>
              <p className="text-2xl font-bold">{summaryStats.totalAffiliates}</p>
              <p className="text-xs text-muted-foreground">Active: {summaryStats.activeAffiliates}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-lg bg-win/10">
                <DollarSign className="h-5 w-5 text-win" />
              </div>
              <Badge variant="secondary" className="bg-win/10 text-win border-0 gap-1">
                <TrendingUp className="h-3 w-3" />
                +12.5%
              </Badge>
            </div>
            <div className="mt-3">
              <p className="text-xs text-muted-foreground">คอมมิชชั่นรวม</p>
              <p className="text-2xl font-bold text-win">{formatCurrency(summaryStats.totalCommission)}</p>
              <p className="text-xs text-warning">รอจ่าย: {formatCurrency(summaryStats.pendingCommission)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <UserPlus className="h-5 w-5 text-blue-500" />
              </div>
              <Badge variant="secondary" className="bg-win/10 text-win border-0 gap-1">
                <TrendingUp className="h-3 w-3" />
                +{summaryStats.newMembers}
              </Badge>
            </div>
            <div className="mt-3">
              <p className="text-xs text-muted-foreground">สมาชิกจากพันธมิตร</p>
              <p className="text-2xl font-bold">{formatNumber(summaryStats.totalMembers)}</p>
              <p className="text-xs text-muted-foreground">ใหม่เดือนนี้: +{formatNumber(summaryStats.newMembers)}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <Percent className="h-5 w-5 text-purple-500" />
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-muted-foreground">Conversion Rate เฉลี่ย</p>
              <p className="text-2xl font-bold">{summaryStats.avgConversion}%</p>
              <p className="text-xs text-muted-foreground">ยอดฝากรวม: {formatCurrency(summaryStats.totalDeposit)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        {performanceMetrics.map((metric, idx) => (
          <Card key={idx} className="bg-muted/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{metric.label}</p>
                <Badge variant="secondary" className={cn(
                  "border-0 text-xs",
                  metric.positive ? "bg-win/10 text-win" : "bg-destructive/10 text-destructive"
                )}>
                  {metric.change}
                </Badge>
              </div>
              <p className="text-xl font-bold mt-2">{metric.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Top Affiliates */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5" />
              Top 5 พันธมิตร
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topAffiliates.map((affiliate) => (
                <div key={affiliate.rank} className="flex items-center gap-4 p-3 rounded-lg bg-muted/30">
                  <div className={cn(
                    "h-10 w-10 rounded-full flex items-center justify-center font-bold text-lg",
                    affiliate.rank === 1 && "bg-yellow-500/20 text-yellow-500",
                    affiliate.rank === 2 && "bg-gray-400/20 text-gray-400",
                    affiliate.rank === 3 && "bg-orange-600/20 text-orange-600",
                    affiliate.rank > 3 && "bg-muted text-muted-foreground"
                  )}>
                    {affiliate.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{affiliate.name}</p>
                      <Badge variant="secondary" className="text-xs bg-muted">{affiliate.code}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                      <span>{formatNumber(affiliate.members)} สมาชิก</span>
                      <span>•</span>
                      <span>{formatCurrency(affiliate.deposit)} ฝาก</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-win">{formatCurrency(affiliate.commission)}</p>
                    <div className={cn(
                      "text-xs flex items-center justify-end gap-1",
                      affiliate.growth >= 0 ? "text-win" : "text-destructive"
                    )}>
                      {affiliate.growth >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                      {affiliate.growth >= 0 ? '+' : ''}{affiliate.growth}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Level Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5" />
              การกระจายตามระดับ
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {levelDistribution.map((level) => (
                <div key={level.level} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{level.icon}</span>
                      <span className="font-medium">{level.level}</span>
                      <Badge variant="secondary" className="text-xs bg-muted">{level.count} คน</Badge>
                    </div>
                    <span className="font-semibold text-win">{formatCurrency(level.commission)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Progress value={level.percent} className="h-2 flex-1" />
                    <span className="text-xs text-muted-foreground w-12 text-right">{level.percent}%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            แนวโน้มรายเดือน
          </CardTitle>
          <CardDescription>ยอดพันธมิตร สมาชิก และค่าคอมมิชชั่น 6 เดือนล่าสุด</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-6 gap-4">
            {monthlyTrend.map((month) => (
              <div key={month.month} className="text-center">
                <div className="h-32 flex flex-col justify-end mb-2">
                  <div 
                    className="bg-primary/60 rounded-t-sm mx-auto w-8"
                    style={{ height: `${(month.commission / maxCommission) * 100}%` }}
                  />
                </div>
                <p className="text-xs font-medium">{month.month}</p>
                <p className="text-xs text-muted-foreground">{formatCurrency(month.commission)}</p>
                <div className="flex items-center justify-center gap-1 text-xs mt-1">
                  <Users className="h-3 w-3 text-muted-foreground" />
                  <span>{formatNumber(month.members)}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}










